// component 1: AllStatic.jsx (Revised - Removed ID and CreatedAt from display)
import React, { useState, useCallback, useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import { storage } from "../../../firebase/config";
import {
  ref,
  deleteObject,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import Loader from "../../loader/Loader";
import { useStaticData } from "../../../CustomHooks/StaticListProvider";
import "react-toastify/dist/ReactToastify.css";
import { FaPlus, FaSpinner, FaTrashAlt } from "react-icons/fa";
import AddItemForm from "./AddItemForm"; // Import the form

const capitalizeFirstLetter = (string) => {
  if (!string) return string;
  return string.charAt(0).toUpperCase() + string.slice(1);
};

// Helper to determine the image field key (adjust as needed)
const getImageFieldKey = (itemOrFieldName) => {
  if (typeof itemOrFieldName === "string") {
    // Guess based on fieldName if item not available
    if (itemOrFieldName === "featuredProducts") return "image";
    if (itemOrFieldName === "logos") return "imageUrl";
    if (itemOrFieldName === "stores") return "pictureUrl";
    return "imageUrl"; // Default
  }
  // Determine from existing item keys
  const imageFieldKeys = ["imageUrl", "pictureUrl", "image"];
  for (const fieldName of imageFieldKeys) {
    if (
      itemOrFieldName &&
      typeof itemOrFieldName[fieldName] === "string" &&
      itemOrFieldName[fieldName].startsWith("http")
    ) {
      return fieldName;
    }
  }
  return null; // No image field found
};

const AllStatic = () => {
  const [processingItemId, setProcessingItemId] = useState(null); // For delete/add loading state
  const [isSaving, setIsSaving] = useState(false); // Specific state for Add modal saving
  const [modalInfo, setModalInfo] = useState({
    isOpen: false,
    fieldName: null, // Changed from collectionName to fieldName
  });
  const { defaultContent } = useStaticData(); // No need for refreshData from context if hook handles state

  // --- DELETE ITEM ---
  // Keep deleteItemHandler as is, it needs the item.id to delete from the backend
  const deleteItemHandler = useCallback(
    async (fieldName, itemToDelete) => {
      // Use the fieldName (key) to get the correct context actions
      const contentActions = defaultContent[fieldName];
      if (!contentActions || typeof contentActions.deleteItem !== "function") {
        toast.error(
          `Configuration error: Delete action not found for ${fieldName}.`
        );
        return;
      }

      const imageField = getImageFieldKey(itemToDelete);
      const imageUrlToDelete = imageField ? itemToDelete[imageField] : null;

      if (
        !window.confirm(
          `Are you sure you want to delete this item from ${fieldName}?`
        )
      ) {
        return;
      }

      setProcessingItemId(itemToDelete.id); // Indicate processing started for this item
      try {
        // 1. Delete image from Storage (if applicable)
        if (imageUrlToDelete) {
          try {
            const imageRef = ref(storage, imageUrlToDelete);
            await deleteObject(imageRef);
            console.log("Storage image deleted successfully.");
          } catch (storageError) {
            console.warn("Could not delete image from Storage:", storageError);
            // Allow proceeding even if image deletion fails (e.g., object not found)
            if (storageError.code !== "storage/object-not-found") {
              toast.warn(
                `Could not delete image, but proceeding with data deletion.`
              );
            }
          }
        }

        // 2. Delete item from Firestore array using the hook's function
        await contentActions.deleteItem(itemToDelete); // Pass the exact item object

        toast.success(
          `${capitalizeFirstLetter(fieldName)} item successfully deleted!`
        );
        // No manual refresh needed - the hook updates the state
      } catch (error) {
        console.error(`Error deleting ${fieldName} item:`, error);
        toast.error(`Error deleting item: ${error.message}`);
        // Optionally: Manually trigger fetch again via contentActions.fetchData() if needed on error
      } finally {
        setProcessingItemId(null); // Clear processing state
      }
    },
    [defaultContent] // Dependency: context data
  );

  // --- ADD ITEM ---
  // Keep handleAddItem as is. It passes data *without* id/createdAt to the hook.
  // The hook is responsible for adding ID and timestamp.
  const handleAddItem = useCallback(
    async (fieldName, newItemData, imageFile) => {
      const contentActions = defaultContent[fieldName];
      if (!contentActions || typeof contentActions.addItem !== "function") {
        toast.error(
          `Configuration error: Add action not found for ${fieldName}.`
        );
        setIsSaving(false); // Ensure saving state is reset
        return;
      }

      setIsSaving(true);
      let dataToSave = { ...newItemData }; // Start with form data

      try {
        // 1. Upload image if provided
        if (imageFile) {
          // Determine the correct image field name for this section
          const imageFieldName = getImageFieldKey(fieldName) || "imageUrl"; // Default if not specific

          const storageRef = ref(
            storage,
            `${fieldName}/${Date.now()}_${imageFile.name}` // Use fieldName for folder structure
          );
          const uploadResult = await uploadBytes(storageRef, imageFile);
          const downloadURL = await getDownloadURL(uploadResult.ref);
          dataToSave[imageFieldName] = downloadURL; // Add image URL to the object
          console.log("Image uploaded, URL:", downloadURL);
        }

        // 2. Add item to Firestore array using the hook's function
        // The hook (useStaticDefaultField) now handles ID and timestamp generation
        const addedItem = await contentActions.addItem(dataToSave); // dataToSave does NOT contain id or createdAt from the form

        toast.success(
          `${capitalizeFirstLetter(fieldName)} added successfully!`
        );
        closeModal();
        // No manual refresh needed - the hook updates the state
      } catch (error) {
        console.error(`Error adding ${fieldName} item:`, error);
        toast.error(`Error adding item: ${error.message}`);
        // Optionally: Trigger fetch via contentActions.fetchData() if needed on error
      } finally {
        setIsSaving(false);
      }
    },
    [defaultContent] // Dependency: context data
  );

  const openModal = (
    fieldName // Takes fieldName now
  ) => setModalInfo({ isOpen: true, fieldName: fieldName });
  const closeModal = () => setModalInfo({ isOpen: false, fieldName: null });

  // --- RENDER TABLE CELL ---
  const renderTableCell = useCallback((item, key) => {
    // Skip rendering for 'id' and 'createdAt' keys
    if (key === "id" || key === "createdAt") {
      return null; // Explicitly return null to render an empty or non-existent cell
    }

    const value = item[key];

    // Image rendering (using helper function)
    const imageField = getImageFieldKey(item);
    if (
      key === imageField &&
      typeof value === "string" &&
      value.startsWith("http")
    ) {
      return (
        <td className="px-5 py-3 align-middle">
          <img
            src={value}
            alt={`${key}`}
            className="h-16 w-auto object-contain rounded"
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
        </td>
      );
    }
    // Array rendering
    if (Array.isArray(value)) {
      return (
        <td className="px-5 py-3 align-top max-w-xs">
          <ul className="list-disc list-inside space-y-1 text-xs">
            {value.map((val, index) => (
              <li key={index} className="break-words">
                {/* Improved handling for objects within arrays */}
                {typeof val === "object" && val !== null
                  ? JSON.stringify(val)
                  : String(val)}
              </li>
            ))}
          </ul>
        </td>
      );
    }

    if (
      typeof value === "object" &&
      value !== null &&
      !(value instanceof Date) // Check specifically for Date instances
    ) {
      return (
        <td className="px-5 py-3 break-words max-w-xs text-xs align-middle">
          {JSON.stringify(value)}
        </td>
      );
    }
    // Default string rendering (will now handle dates as strings if not explicitly rendered as Date)
    return (
      <td className="px-5 py-3 break-words max-w-sm align-middle">
        {String(value)}
      </td>
    );
  }, []); // No dependencies needed as it only operates on passed item and key

  // --- GET TABLE HEADERS ---
  const getTableHeaders = useCallback((list) => {
    if (!list || list.length === 0) return [];
    const firstItemKeys = Object.keys(list[0]);
    const preferredOrder = [
      "image",
      "imageUrl",
      "pictureUrl", // Image fields first
      "header",
      "title",
      "name",
      "promise", // Common text fields
      "description",
      "features",
      "location",
      "gradient", // Others
    ];
    const knownKeys = new Set(preferredOrder);
    // Removed explicit addition of 'id' and 'createdAt' to knownKeys

    // Filter out 'id' and 'createdAt' from the initial keys
    const filteredKeys = firstItemKeys.filter(
      (k) => k !== "id" && k !== "createdAt" && k !== "action" // Exclude action pseudo-key
    );

    // Build the final order, prioritizing preferred keys that exist
    let orderedKeys = [
      ...preferredOrder.filter((k) => filteredKeys.includes(k)), // Preferred keys present in the item (excluding id/createdAt)
      ...filteredKeys.filter((k) => !preferredOrder.includes(k)), // Other keys found (excluding id/createdAt)
    ];

    // Ensure uniqueness (though filtering should handle most duplicates here)
    orderedKeys = [...new Set(orderedKeys)];

    return orderedKeys;
  }, []); // No dependencies needed as it operates on passed list

  // --- JSX STRUCTURE ---
  return (
    <div className="p-4 md:p-8 min-h-screen font-sans">
      <ToastContainer
        position="bottom-right"
        autoClose={3000}
        theme="colored"
      />
      <h1 className="text-3xl font-semibold text-gray-800 text-center mb-8">
        Website Designing
      </h1>

      <div className="space-y-10">
        {/* Iterate through the fields managed by useStaticDefaultField */}
        {Object.entries(defaultContent).map(([fieldName, contentData]) => {
          // Basic check if contentData seems valid (has list, loading etc.)
          if (
            !contentData ||
            typeof contentData !== "object" ||
            !("list" in contentData)
          ) {
            console.warn(
              `Skipping section "${fieldName}": Invalid data structure in context.`
            );
            return null;
          }

          const { list, loading, error } = contentData; // Destructure state from the hook's return
          const tableHeaders = getTableHeaders(list); // Get headers based on available keys (excluding id/createdAt)

          return (
            <div
              key={fieldName} // Use fieldName as the key
              className="bg-white p-5 shadow-lg rounded-lg border border-gray-200"
            >
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-5 gap-3">
                <h2 className="text-2xl font-semibold text-gray-700">
                  {capitalizeFirstLetter(fieldName)}
                </h2>
                <button
                  onClick={() => openModal(fieldName)} // Pass fieldName
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 transition duration-150 ease-in-out shadow-sm"
                >
                  <FaPlus />
                  Add New {capitalizeFirstLetter(fieldName)} Item
                </button>
              </div>

              {loading && (
                <div className="flex justify-center items-center h-32">
                  <Loader />
                </div>
              )}

              {error && (
                <div className="text-red-700 bg-red-100 p-4 rounded border border-red-300">
                  Error loading {fieldName}: {error.message || String(error)}
                </div>
              )}

              {!loading && !error && (
                <div className="overflow-x-auto">
                  {list.length > 0 ? (
                    <table className="w-full text-sm text-left text-gray-600">
                      <thead className="text-xs text-gray-700 uppercase bg-gray-100 border-b">
                        <tr>
                          {tableHeaders.map((headerKey) => (
                            <th
                              key={headerKey} // Use headerKey as the key
                              scope="col"
                              className="px-5 py-3 font-medium"
                            >
                              {capitalizeFirstLetter(headerKey)}
                            </th>
                          ))}
                          {/* Always add Actions header */}
                          <th
                            scope="col"
                            className="px-5 py-3 font-medium text-center"
                          >
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {list.map(
                          (
                            item // item should have a unique 'id' from the hook
                          ) => (
                            <tr
                              key={item.id} // Use the generated unique ID for the row key
                              className="hover:bg-gray-50 transition duration-150 ease-in-out"
                            >
                              {tableHeaders.map((headerKey) => (
                                // Use item.id and headerKey for unique cell key
                                <React.Fragment key={`${item.id}-${headerKey}`}>
                                  {renderTableCell(item, headerKey)}
                                </React.Fragment>
                              ))}
                              <td className="px-5 py-3 text-center align-middle">
                                <button
                                  onClick={() =>
                                    deleteItemHandler(fieldName, item)
                                  } // Pass fieldName and the item object (including ID for deletion)
                                  className={`p-2 text-red-600 hover:text-red-800 rounded-md hover:bg-red-100 transition duration-150 ease-in-out ${
                                    processingItemId === item.id
                                      ? "opacity-50 cursor-wait"
                                      : ""
                                  }`}
                                  disabled={processingItemId === item.id}
                                  aria-label={`Delete ${fieldName} item`}
                                >
                                  {processingItemId === item.id ? (
                                    <FaSpinner className="animate-spin text-red-600 h-4 w-4" />
                                  ) : (
                                    <FaTrashAlt className="w-4 h-4" />
                                  )}
                                </button>
                              </td>
                            </tr>
                          )
                        )}
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-center py-6 text-gray-500">
                      No {fieldName} items found. Add one!
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* --- MODAL --- */}
      {modalInfo.isOpen &&
        modalInfo.fieldName && ( // Check for fieldName too
          <div className="fixed inset-0 z-40 flex items-center justify-center p-4 bg-black bg-opacity-60 backdrop-blur-sm">
            <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto relative">
              <button
                onClick={closeModal}
                className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 text-2xl"
                aria-label="Close modal"
              >
                ×
              </button>
              <AddItemForm
                fieldName={modalInfo.fieldName} // Pass fieldName
                onSave={(newItemData, imageFile) =>
                  handleAddItem(modalInfo.fieldName, newItemData, imageFile)
                }
                onCancel={closeModal}
                isSaving={isSaving}
              />
            </div>
          </div>
        )}
    </div>
  );
};

export default AllStatic;
